import React, { useMemo, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { generateAccountStatement } from "api/individual-user-management";
import {
  IGenerateAccountStatementPayload,
  IIndividualAccount,
  IIndividualAccountsData,
} from "api/individual-user-management/types";
import { ReactComponent as MenuDotsIcon } from "assets/icons/menu-dots.svg";
import { useIndividuals } from "contexts/individuals-context";
import {
  IMoreQuickActionItem, // IMoreQuickActionsProps,
  TQuickActionType,
} from "features/individuals/individual-details/more-quick-actions/types";
import {
  individualDetailsQuickActionBtnCss3,
  moreQuickActionsDropDownPopUpMenuContentCss3,
} from "features/individuals/individual-details/styles";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { formatDateToYYYYMMDD, handleDownloadBufferData } from "utils";

import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { DropDown } from "ui/dropdown";
import { IDropDownRef } from "ui/dropdown/types";
import {
  DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA,
  GenerateAccountStatementModal,
} from "ui/generate-account-statement-modal";
import { TTableRowData } from "ui/table/types";
// import { TTableRowData } from "ui/table/types";
import { Paragraph3 } from "ui/typography";

import { INDIVIDUAL_ACCOUNT_NAME_IN_URL_SEPARATOR } from "../../consts";
import { ApplyPostNoDebitModal } from "./ApplyPostNoDebitModal";
import { RemovePostNoDebitModal } from "./RemovePostNoDebitModal";

const textColorMap: Record<TQuickActionType, string> = {
  regular: "#1A202C",
  disabled: "#344054",
  danger: "#DC2626",
};
const DROP_DOWN_WIDTH = 240;

interface INewMoreQuickActionsProps {
  individualDetails: IIndividualAccountsData;
  handleViewIndividualDetails: (individualRowData: TTableRowData) => void;
  walletId: number;
}

// interface INewMoreQuickActionsProps extends IMoreQuickActionsProps {
//   handleViewIndividualDetails: (individualRowData: TTableRowData) => void;
// }

export const MoreQuickActions = (props: INewMoreQuickActionsProps) => {
  const { individualDetails, walletId } = props;
  const individualId = useParams().individualId;
  const { userDetailsData } = useIndividuals();
  const navigate = useNavigate();
  const { setCurrentAccountDetails, currentAccountDetails } = useIndividuals();

  const [isRemovePostNoDebitModalOpen, setIsRemovePostNoDebitModalOpen] =
    useState(false);
  const [isApplyPostNoDebitModalOpen, setIsApplyPostNoDebitModalOpen] =
    useState(false);
  const [
    isGenerateAccountStatementModalOpen,
    setIsGenerateAccountStatementModalOpen,
  ] = useState(false);
  const [
    generateAccountStatementFormData,
    setGenerateAccountStatementFormData,
  ] = useState(DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA);
  const [isGenerating, setIsGenerating] = useState(false);
  const dropDownRef = useRef<IDropDownRef | null>(null);

  const handleToggleRemovePostNoDebitModal = () =>
    setIsRemovePostNoDebitModalOpen(!isRemovePostNoDebitModalOpen);
  const handleToggleApplyPostNoDebitModal = () =>
    setIsApplyPostNoDebitModalOpen(!isApplyPostNoDebitModalOpen);
  const handleToggleGenerateAccountStatementModal = () =>
    setIsGenerateAccountStatementModalOpen(
      !isGenerateAccountStatementModalOpen
    );
  const handleToggleDropDown = () => {
    if (!dropDownRef.current) return;
    dropDownRef.current.handleToggleDropdown();
  };

  const handleGenerateAccountStatement = async () => {
    const { startDate, endDate, recipient } = generateAccountStatementFormData;
    if (
      !currentAccountDetails?.userId ||
      recipient === "" ||
      !startDate ||
      !endDate
    ) {
      return;
    }

    const payload: IGenerateAccountStatementPayload = {
      walletId: String(walletId),
      startDate: formatDateToYYYYMMDD(startDate),
      endDate: formatDateToYYYYMMDD(endDate),
      sendType: recipient,
      email: userDetailsData.email,
    };
    setIsGenerating(true);
    try {
      const { error, message, data } = await generateAccountStatement(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        if (data?.data) {
          handleDownloadBufferData({
            data: data?.data,
            name: `${currentAccountDetails?.accountName || "N/A"}'s account statement`,
            type: "pdf",
          });
        }
        customSuccessToast({
          message,
        });
        setGenerateAccountStatementFormData(
          DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA
        );
        handleToggleGenerateAccountStatementModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsGenerating(false);
  };

  const quickActions: IMoreQuickActionItem[] = useMemo(() => {
    if (!individualDetails) return [];
    // const { id, isActive } = individualDetails;
    const currentDetails = individualDetails.data.find(
      ({ userId }) => userId === individualId
    );

    setCurrentAccountDetails(currentDetails as IIndividualAccount);

    const { id, status: isActive, accountName } = currentDetails || {};
    const handleAccountDetails = async () => {
      setCurrentAccountDetails(currentDetails as IIndividualAccount);
      if (accountName) {
        navigate(
          `${ROUTES.INDIVIDUALS_PAGE}/account/${accountName.replace(/ /g, INDIVIDUAL_ACCOUNT_NAME_IN_URL_SEPARATOR)}/${id}/view-transactions/${props.walletId}`
          // `${ROUTES.INDIVIDUALS_PAGE}/account/${userId}/view-transactions/${props.walletId}`
        );
      }
    };
    return [
      {
        label: "View Transactions",
        isHidden: false,
        handleQuickActionItemClick: () => {
          handleAccountDetails();
          handleToggleDropDown();
          // console.log("currentAccountDetails:", currentAccountDetails);
          // console.log("hello");
          // navigate(
          //   `${ROUTES.INDIVIDUALS_PAGE}/account/${id}/view-transactions/${props.walletId}`
          // );
        },
        type: "disabled",
      },
      {
        label: "Generate Account Statement",
        isHidden: false,
        handleQuickActionItemClick: () => {
          handleToggleDropDown();
          handleToggleGenerateAccountStatementModal();
        },
        type: "regular",
      },
      {
        label: isActive !== "active" ? "Unfreeze" : "Total Freeze",
        isHidden: false,
        handleQuickActionItemClick: () => {
          handleToggleDropDown();
          if (isActive !== "active") {
            handleToggleRemovePostNoDebitModal();
          } else {
            handleToggleApplyPostNoDebitModal();
          }
        },
        type: isActive !== "active" ? "regular" : "danger",
      },
    ];
  }, [individualDetails]);

  return (
    <>
      <DropDown
        dropDownWidth={DROP_DOWN_WIDTH}
        dropDownBtnContent={
          <>
            <MenuDotsIcon className="w-4 h-4" />
          </>
        }
        allowOutSideClick
        customDropDownButtonCss={individualDetailsQuickActionBtnCss3}
        dropDownPopUpMenuContentCss={
          moreQuickActionsDropDownPopUpMenuContentCss3
        }
        ref={dropDownRef}
      >
        <Box className="flex flex-col">
          {quickActions.map(
            ({ label, isDisabled, handleQuickActionItemClick, type }, idx) => {
              return (
                <Box
                  className={`h-10 py-[10px] flex items-center ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                  key={idx}
                  onClick={handleQuickActionItemClick}
                >
                  <Paragraph3
                    textColor={textColorMap[type]}
                    lineHeight="20px"
                    fontWeight="600"
                  >
                    {label}
                  </Paragraph3>
                </Box>
              );
            }
          )}
        </Box>
      </DropDown>
      <GenerateAccountStatementModal
        isOpen={isGenerateAccountStatementModalOpen}
        setIsOpen={setIsGenerateAccountStatementModalOpen}
        handleToggleModal={handleToggleGenerateAccountStatementModal}
        formData={generateAccountStatementFormData}
        setFormData={setGenerateAccountStatementFormData}
        handleGenerateAccountStatement={handleGenerateAccountStatement}
        isLoading={isGenerating}
      />
      <RemovePostNoDebitModal
        isOpen={isRemovePostNoDebitModalOpen}
        setIsOpen={setIsRemovePostNoDebitModalOpen}
        handleToggleRemovePostNoDebitModal={handleToggleRemovePostNoDebitModal}
        walletId={walletId}
      />
      <ApplyPostNoDebitModal
        isOpen={isApplyPostNoDebitModalOpen}
        setIsOpen={setIsApplyPostNoDebitModalOpen}
        handleToggleApplyPostNoDebitModal={handleToggleApplyPostNoDebitModal}
        walletId={walletId}
      />
    </>
  );
};
